import { useEffect, useMemo, useRef, useState } from "react"
import { Alert, Button, Card, Col, Form, Row, Spinner } from "react-bootstrap"
import DataTable from "react-data-table-component"
import { useAuth } from "../../../auth/auth-context"
import useFetch from "../../../_fetch"
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts"

type ActivityPoint = {
    date: string
    [key: string]: string | number
}

type ActivityResponse = {
    fromDate: string
    toDate: string
    totalEvents: number
    totalEventLogs?: number
    filesProcessed: number
    users: string[]
    data: ActivityPoint[]
}

type UserSummaryRow = {
    username: string
    activeDays: number
    events: number
}

const USER_COLORS = [
    "#1b9e77",
    "#d95f02",
    "#7570b3",
    "#e7298a",
    "#66a61e",
    "#e6ab02",
    "#a6761d",
    "#1f78b4",
    "#b2df8a",
    "#fb9a99",
    "#fdbf6f",
    "#cab2d6",
    "#6a3d9a",
    "#ff7f00",
    "#b15928",
    "#17becf",
    "#bcbd22",
    "#8c564b",
    "#9467bd",
    "#2ca02c",
    "#ff9896",
    "#98df8a",
    "#9edae5",
    "#c7c7c7",
]

function toDateInput(date: Date) {
    const y = date.getUTCFullYear()
    const m = String(date.getUTCMonth() + 1).padStart(2, "0")
    const d = String(date.getUTCDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
}

export default function AdminHome() {
    const { user } = useAuth()
    const { doFetch } = useFetch()

    const today = useMemo(() => toDateInput(new Date()), [])
    const defaultFrom = useMemo(() => {
        const d = new Date()
        d.setUTCMonth(d.getUTCMonth() - 2)
        return toDateInput(d)
    }, [])

    const [fromDate, setFromDate] = useState(defaultFrom)
    const [toDate, setToDate] = useState(today)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState("")
    const [activity, setActivity] = useState<ActivityResponse | null>(null)
    const [hiddenUsers, setHiddenUsers] = useState<string[]>([])
    const [isolatedUser, setIsolatedUser] = useState<string | null>(null)
    const legendClickRef = useRef<{ username: string | null; timer: any }>({ username: null, timer: null })

    const sortedUsersAlphabetically = useMemo(() => {
        if (!activity?.users?.length) return []
        return [...activity.users].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
    }, [activity])

    const userColorByName = useMemo(() => {
        const map = new Map<string, string>()
        sortedUsersAlphabetically.forEach((name, idx) => {
            map.set(name, USER_COLORS[idx % USER_COLORS.length])
        })
        return map
    }, [sortedUsersAlphabetically])

    const usersToRender = sortedUsersAlphabetically

    const userSummaryRows = useMemo<UserSummaryRow[]>(() => {
        if (!activity) return []

        const rows = new Map<string, UserSummaryRow>()
        for (const username of activity.users) {
            rows.set(username, { username, activeDays: 0, events: 0 })
        }

        for (const point of activity.data) {
            for (const username of activity.users) {
                const value = Number(point[username] || 0)
                const row = rows.get(username)
                if (!row) continue
                row.events += value
                if (value > 0) row.activeDays += 1
            }
        }

        return Array.from(rows.values()).sort((a, b) => b.events - a.events)
    }, [activity])

    const summaryColumns = [
        {
            id: 1,
            name: "Username",
            selector: (row: UserSummaryRow) => row.username,
            sortable: true,
            wrap: true,
        },
        {
            id: 2,
            name: "Active days",
            selector: (row: UserSummaryRow) => row.activeDays,
            sortable: true,
            wrap: true,
            right: true,
        },
        {
            id: 3,
            name: "Events",
            selector: (row: UserSummaryRow) => row.events,
            sortable: true,
            wrap: true,
            right: true,
        },
    ]

    const isUserVisible = (username: string) => {
        if (isolatedUser) return username === isolatedUser
        return !hiddenUsers.includes(username)
    }

    const toggleHiddenUser = (username: string) => {
        setIsolatedUser(null)
        setHiddenUsers((prev) =>
            prev.includes(username) ? prev.filter((v) => v !== username) : [...prev, username]
        )
    }

    const isolateUser = (username: string) => {
        setHiddenUsers([])
        setIsolatedUser((prev) => (prev === username ? null : username))
    }

    const handleLegendClick = (username: string) => {
        const pending = legendClickRef.current

        if (pending.username === username && pending.timer) {
            clearTimeout(pending.timer)
            legendClickRef.current = { username: null, timer: null }
            isolateUser(username)
            return
        }

        if (pending.timer) {
            clearTimeout(pending.timer)
            if (pending.username) {
                toggleHiddenUser(pending.username)
            }
        }

        const timer = setTimeout(() => {
            toggleHiddenUser(username)
            legendClickRef.current = { username: null, timer: null }
        }, 220)

        legendClickRef.current = { username, timer }
    }

    const loadActivity = async (from: string, to: string) => {
        setIsLoading(true)
        setError("")
        try {
            const params = new URLSearchParams({ fromDate: from, toDate: to, metric: "events" })
            const res = await doFetch(`/user/admin/activity?${params.toString()}`)
            if (res.status >= 400) {
                throw new Error(await res.text())
            }
            const data: ActivityResponse = await res.json()
            setActivity(data)
            setHiddenUsers([])
            setIsolatedUser(null)
        } catch (err: any) {
            setError(err?.message || "Failed to load activity")
            setActivity(null)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        loadActivity(defaultFrom, today)
    }, [defaultFrom, today])

    useEffect(() => {
        return () => {
            if (legendClickRef.current.timer) {
                clearTimeout(legendClickRef.current.timer)
            }
        }
    }, [])

    const onApply = () => {
        if (!fromDate || !toDate) {
            setError("Please choose both from and to date")
            return
        }
        if (new Date(fromDate) > new Date(toDate)) {
            setError("From date must be before to date")
            return
        }
        loadActivity(fromDate, toDate)
    }

    return (
        <div className="p-3 p-md-4">
            <Card className="border-0 shadow-sm mb-4">
                <Card.Body className="p-4">
                    <h2 className="mb-2">Welcome, {user?.username || "admin"}</h2>
                    <p className="text-muted mb-0">
                        This is your admin home. Review user activity trends across the selected period.
                    </p>
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mt-3">
                <Card.Body>
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                        <h5 className="mb-0">Activity</h5>
                        {activity ? (
                            <small className="text-muted">
                                {activity.totalEventLogs ?? activity.totalEvents} log events across {activity.users.length} users
                            </small>
                        ) : null}
                    </div>

                    <Row className="g-2 align-items-end mb-3">
                        <Col xs={12} md={5}>
                            <Form.Label className="small text-muted mb-1">From date</Form.Label>
                            <Form.Control
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                            />
                        </Col>
                        <Col xs={12} md={5}>
                            <Form.Label className="small text-muted mb-1">To date</Form.Label>
                            <Form.Control
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                            />
                        </Col>
                        <Col xs={12} md={2}>
                            <Button variant="dark" onClick={onApply} disabled={isLoading}>
                                {isLoading ? "Loading..." : "Apply period"}
                            </Button>
                        </Col>
                    </Row>

                    <div className="small text-muted mb-3">
                        Click legend to toggle a user. Double-click legend to isolate one user. Values are event counts per day.
                    </div>

                    {error ? <Alert variant="danger" className="mb-3">{error}</Alert> : null}

                    {isLoading && !activity ? (
                        <div className="d-flex align-items-center gap-2 text-muted">
                            <Spinner animation="border" size="sm" /> Loading activity data...
                        </div>
                    ) : activity ? (
                        <div className="rounded-3 p-2 p-md-3" style={{ background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)" }}>
                            <div className="d-flex flex-wrap gap-2 mb-3">
                                <span className="badge text-bg-light border">From: {activity.fromDate}</span>
                                <span className="badge text-bg-light border">To: {activity.toDate}</span>
                                <span className="badge text-bg-light border">Users: {activity.users.length}</span>
                            </div>

                            <div style={{ width: "100%", height: 360 }}>
                                <ResponsiveContainer>
                                    <LineChart data={activity.data} margin={{ top: 12, right: 24, left: 8, bottom: 8 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                        <XAxis dataKey="date" minTickGap={24} tick={{ fill: "#475569", fontSize: 12 }} />
                                        <YAxis allowDecimals={false} tick={{ fill: "#475569", fontSize: 12 }} />
                                        <Tooltip
                                            contentStyle={{ borderRadius: 10, borderColor: "#e2e8f0" }}
                                            formatter={(value: number, name: string) => [value, name]}
                                        />
                                        <Legend
                                            wrapperStyle={{ fontSize: 12 }}
                                            content={({ payload }) => (
                                                <div className="d-flex flex-wrap gap-2 mt-2">
                                                    {(payload || []).map((entry: any) => {
                                                        const username = String(entry?.value || entry?.dataKey || "")
                                                        if (!username) return null
                                                        const visible = isUserVisible(username)

                                                        return (
                                                            <button
                                                                key={username}
                                                                type="button"
                                                                onClick={() => handleLegendClick(username)}
                                                                className="btn btn-sm"
                                                                style={{
                                                                    border: `1px solid ${entry?.color || "#94a3b8"}`,
                                                                    color: entry?.color || "#334155",
                                                                    backgroundColor: visible ? "#f8fafc" : "#e2e8f0",
                                                                    opacity: visible ? 1 : 0.5,
                                                                }}
                                                            >
                                                                {username}
                                                            </button>
                                                        )
                                                    })}
                                                </div>
                                            )}
                                        />

                                        {usersToRender.map((username) => (
                                            <Line
                                                key={username}
                                                type="monotone"
                                                dataKey={username}
                                                stroke={userColorByName.get(username) || "#64748b"}
                                                strokeWidth={1.8}
                                                dot={false}
                                                hide={!isUserVisible(username)}
                                                name={username}
                                            />
                                        ))}
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>

                            <div className="mt-4">
                                <h6 className="mb-2">User summary</h6>
                                <DataTable
                                    columns={summaryColumns as any}
                                    data={userSummaryRows}
                                    dense
                                    pagination
                                    paginationPerPage={50}
                                    paginationRowsPerPageOptions={[50, 100, 200]}
                                    persistTableHead
                                    responsive
                                    defaultSortFieldId={2}
                                    defaultSortAsc={false}
                                />
                            </div>
                        </div>
                    ) : null}
                </Card.Body>
            </Card>
        </div>
    )
}
